import {
  ApolloClient,
  NormalizedCacheObject,
  ApolloError,
  OperationVariables,
  ApolloQueryResult,
} from '@apollo/client'
// Error emission now handled centrally in utils.emitGraphQLError
import { emitGraphQLError } from './errors'
import { buildGraphQLQuery } from './utils'
import type {
  GraphQLFetchOptions,
  GraphQLFetchServiceResult,
  GraphQLResourceFetchOptions,
  GraphQLResourceFetchServiceResult,
} from './types'

// Deprecated: inline handlers replaced by centralized emitGraphQLError

/**
 * Service function for executing GraphQL queries with direct query strings.
 * This function does NOT manage React state and is suitable for use outside React components.
 *
 * @param client - Apollo client instance to use for the query
 * @param options - Query options including query string, variables, and fetch policy
 * @returns Promise with the query result and any errors
 */
export const executeGraphQLFetch = async <T = unknown>(
  client: ApolloClient<NormalizedCacheObject>,
  options: GraphQLFetchOptions,
): Promise<GraphQLFetchServiceResult<T>> => {
  try {
    const result = await client.query<T>({
      query: options.query,
      variables: options.variables || {},
      fetchPolicy: options.fetchPolicy || 'network-only',
      errorPolicy: 'all',
    })

    return {
      data: result.data || null,
      error: result.error || null,
    }
  } catch (err) {
    const error = err as ApolloError
    emitGraphQLError(error, {
      queryInfo: options.query.toString(),
      operation: 'query',
    })
    return {
      data: null,
      error,
    }
  }
}

/**
 * Service function for refetching GraphQL queries with optional variable overrides.
 * This function does NOT manage React state and is suitable for use outside React components.
 *
 * @param client - Apollo client instance to use for the query
 * @param options - Base query options
 * @param overrideVariables - Optional variables to override in the query
 * @returns Promise with the refetch result
 */
export const refetchGraphQLFetch = async <T = unknown>(
  client: ApolloClient<NormalizedCacheObject>,
  options: GraphQLFetchOptions,
  overrideVariables?: OperationVariables,
): Promise<ApolloQueryResult<T>> => {
  const finalVariables = {
    ...options.variables,
    ...overrideVariables,
  }

  return client.query<T>({
    query: options.query,
    variables: finalVariables,
    fetchPolicy: 'network-only', // Force refetch
    errorPolicy: 'all',
  })
}

/**
 * Service function for executing resource-based GraphQL queries.
 * This function does NOT manage React state and is suitable for use outside React components.
 *
 * @param client - Apollo client instance to use for the query
 * @param options - Resource query options including resource name, UID, fields, etc.
 * @returns Promise with the query result and any errors
 */
export const executeGraphQLResourceFetch = async <T = unknown>(
  client: ApolloClient<NormalizedCacheObject>,
  options: GraphQLResourceFetchOptions,
): Promise<GraphQLResourceFetchServiceResult<T>> => {
  try {
    const queryInfo = buildGraphQLQuery({
      resourceName: options.resourceName,
      objectUid: options.objectUid,
      fragment: options.fragment,
      fields: options.fields,
      variables: options.variables || {},
    })

    const result = await client.query<Record<string, T>>({
      query: queryInfo.query,
      variables: queryInfo.finalVariables,
      fetchPolicy: options.fetchPolicy || 'network-only',
      errorPolicy: 'all',
    })

    const data = result.data?.[queryInfo.queryName] || null

    return {
      data,
      error: result.error || null,
    }
  } catch (error) {
    const apolloError = error as ApolloError
    emitGraphQLError(apolloError, {
      resourceName: options.resourceName,
      objectUid: options.objectUid,
      operation: 'query',
      // queryInfo is not available here because it failed before creation
    })

    return {
      data: null,
      error: apolloError,
    }
  }
}

/**
 * Service function for refetching resource-based GraphQL queries with optional variable overrides.
 * This function does NOT manage React state and is suitable for use outside React components.
 *
 * @param client - Apollo client instance to use for the query
 * @param options - Base resource query options
 * @param overrideVariables - Optional variables to override in the query
 * @returns Promise with the refetch result
 */
export const refetchGraphQLResourceFetch = async <T = unknown>(
  client: ApolloClient<NormalizedCacheObject>,
  options: GraphQLResourceFetchOptions,
  overrideVariables?: OperationVariables,
): Promise<ApolloQueryResult<T>> => {
  const queryInfo = buildGraphQLQuery({
    resourceName: options.resourceName,
    objectUid: options.objectUid,
    fragment: options.fragment,
    fields: options.fields,
    variables: {
      ...options.variables,
      ...overrideVariables,
    },
  })

  return client.query<T>({
    query: queryInfo.query,
    variables: queryInfo.finalVariables,
    fetchPolicy: 'network-only', // Force refetch
    errorPolicy: 'all',
  })
}
